'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { BaseButton } from '@7f-ui/components/button/button';
import { LoadingButton } from '@7f-ui/components/button/loading-button';
import { Expandable } from '@7f-ui/components/expandable';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { Price } from '@7f-ui/components/price';
import type { Nexperience } from '@data-access/graphql/schema.types';
import type { NexperienceTimeslotFragment } from '@data-access/lib/gql/experience/documents';
import { ValidateTimeslotDocument } from '@data-access/lib/gql/experience/documents';
import { useGraphQL } from '@data-access/lib/graphql-query';
import { getExperienceCancelationState, useHydratedNow } from '@feat/feat-cart/cancelation';
import { useIsCartCurrencyMismatch } from '@feat/feat-cart/currency';
import { useExperienceDuration } from '@feat/feat-experience/data';
import { CalendarCheck, CaretDown } from '@phosphor-icons/react';
import { parseLocalDate } from '@utils/helpers';
import { formatDate, formatTime } from '@utils/utils-format';
import { shopTestId } from '@utils/utils-shop-test-ids';
import { differenceInMinutes } from 'date-fns';
import { useMemo, useState } from 'react';
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone';
import type { ExperienceDayPrice } from '../_hooks/use-app-experience-day-prices';
import { repriceVariants } from '../_lib/day-prices';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppPriceCategoryCount } from './app-price-category-count';
const EXPERIENCE_OFFERS_LIST_ITEM_LABELS: Record<string, string> = {
  add: 'In den Warenkorb',
  footerPrice: 'Preis inkl. MwSt.',
  noCancelation: 'Keine Stornierung möglich',
  selected: 'Im Warenkorb',
  timeSlotUnavailable: 'Dieser Termin steht leider nicht mehr zu Verfügung. Bitte wähle einen anderen Termin aus oder ändere den Zeitraum.',
  update: 'Auswählen'
};
type ExperienceSelectionInfo = {
  count: number;
  name: string;
  price: number;
  id: string;
  duration: number;
};
export type AppExperienceOffersListItemProps = {
  timeslot: NexperienceTimeslotFragment;
  experience: Pick<Nexperience, 'cancelationDeadline' | 'id' | 'currency'>;
  dayPrice?: ExperienceDayPrice;
  dayPriceFailed?: boolean;
  onSubmit: (timeslot: NexperienceTimeslotFragment) => Promise<void>;
  onExpand?: (timeslot: NexperienceTimeslotFragment) => void;
  mode?: 'add' | 'update';
  isSelected?: boolean;
};
export function AppExperienceOffersListItem({
  timeslot,
  experience,
  dayPrice,
  dayPriceFailed = false,
  mode = 'add',
  onSubmit,
  onExpand,
  isSelected
}: AppExperienceOffersListItemProps) {
  const isCurrencyMismatch = useIsCartCurrencyMismatch(mode === 'update' ? undefined : experience.currency);
  const {
    filter: {
      selectedOptions,
      selectedVariant
    },
    baseVariants
  } = useExperienceFilterContext();
  const variants = useMemo(() => repriceVariants(baseVariants, dayPrice), [baseVariants, dayPrice]);
  const timeslotStartZone = timeslot.startDate.timezone ?? DEFAULT_TIME_ZONE;
  const [expanded, setExpanded] = useState(mode === 'update' || !!isSelected);
  const [lastIsSelected, setLastIsSelected] = useState(isSelected);
  if (lastIsSelected !== isSelected) {
    setLastIsSelected(isSelected);
    if (mode === 'add' && !isSelected) setExpanded(false);
  }
  const selectedVariantDetails = variants.find(variant => variant.id === selectedVariant);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    data: isValidTimeslot,
    refetch: validateTimeslot
  } = useGraphQL(ValidateTimeslotDocument, {
    input: {
      timeslotId: timeslot.id,
      experienceId: experience.id,
      tickets: Object.keys(selectedOptions ?? {}).map(key => ({
        ticketId: key,
        count: selectedOptions?.[key] ?? 0
      }))
    }
  }, {
    enabled: false,
    select
  });
  if (!selectedVariantDetails || !selectedOptions) {
    return;
  }
  const selectionInfo: ExperienceSelectionInfo[] = Object.keys(selectedOptions).map((id: string) => {
    const count = selectedOptions[id];
    const option = selectedVariantDetails.options.find(variantOption => variantOption.id === id);
    return {
      id: option?.id ?? '',
      name: option?.name ?? '',
      price: option?.price ?? 0,
      count,
      duration: option?.duration ?? 0
    };
  });
  const totalPrice = selectionInfo.reduce((sum, {
    price,
    count: count_0
  }) => sum + price * count_0, 0);
  const timeslotStartDate = parseLocalDate(timeslot.startDate);
  const timeslotEndDate = parseLocalDate(timeslot.endDate);
  const durationInMinutes = differenceInMinutes(timeslotEndDate, timeslotStartDate);
  const longestDuration = selectionInfo.reduce((max, {
    duration
  }) => Math.max(max, duration), 0) || durationInMinutes;
  const addCartItem = async () => {
    setIsSubmitting(true);
    try {
      const validTimeslot = await validateTimeslot();
      if (validTimeslot?.data) {
        await onSubmit(timeslot);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleExpand = () => {
    setExpanded(isExpanded => !isExpanded);
    onExpand?.(timeslot);
  };
  return <li>
      {isValidTimeslot === false && <MessageBox type="error" className="mb-8">
          Dieser Termin steht leider nicht mehr zu Verfügung. Bitte wähle einen anderen Termin aus
          oder ändere den Zeitraum.
        </MessageBox>}
      <div className="relative rounded-2xl bg-lightblue p-6 sm:px-8">
        <div className="flex w-full items-center justify-between">
          <div className="flex-1 font-body-copy">
            {`${formatTime(timeslotStartDate, timeslotStartZone)} Uhr`}
            {!!longestDuration && <>
                &nbsp;&#183;&nbsp;
                <ExperienceDuration duration={longestDuration} />
              </>}
          </div>
          <div className={cx('flex space-x-4 transition-all md:absolute md:right-8', expanded ? 'md:top-6 md:translate-y-0' : 'md:top-1/2 md:-translate-y-1/2')}>
            <BaseButton key={2} className={cx('rotate-0 transition-all', expanded && 'rotate-180')} onClick={handleExpand}>
              <CaretDown size={20} />
            </BaseButton>
          </div>
        </div>

        <Expandable expanded={expanded}>
          <div className="mt-4 overflow-hidden border-primary/15 border-y py-4 font-base-copy sm:mr-11">
            <SelectedOptions selectionInfo={selectionInfo} currency={experience.currency} />
          </div>
          <CancelationHint timeslot={timeslot} experience={experience} />
        </Expandable>

        <div className="mt-4 flex justify-between md:h-11">
          <div className="flex flex-col md:flex-row md:items-baseline md:space-x-2">
            <Price className="font-body-copy font-semibold" amount={totalPrice} currency={experience.currency} />
            <span className="block font-base-copy">Preis inkl. MwSt.</span>
          </div>
          <div className={cx('transition-all md:absolute', expanded ? 'md:right-[calc(44px+32px)] md:bottom-6' : 'md:right-[calc(44px+32px)] md:bottom-1/2 md:translate-y-1/2')}>
            <LoadingButton isLoading={isSubmitting} variant="primary" data-testid={shopTestId('ExperienceOffersListItem.add-to-cart')} onClick={addCartItem} disabled={isValidTimeslot === false || isCurrencyMismatch || dayPriceFailed}>
              {EXPERIENCE_OFFERS_LIST_ITEM_LABELS[mode] ?? mode}
            </LoadingButton>
          </div>
        </div>
      </div>
    </li>;
}
type CancelationHintProps = {
  timeslot: NexperienceTimeslotFragment;
  experience: Pick<Nexperience, 'cancelationDeadline'>;
};
function CancelationHint(t0) {
  const $ = _c(3);
  const {
    timeslot,
    experience
  } = t0;
  const now = useHydratedNow();
  const cancelation = getExperienceCancelationState({
    timeslot,
    experience
  }, now);
  if (!cancelation) {
    return null;
  }
  const {
    date,
    zone,
    isCancelable
  } = cancelation;
  let hint = "Keine Stornierung m\xF6glich";
  if (isCancelable === null && date) {
    hint = "";
  } else {
    if (isCancelable && date) {
      hint = `Kostenlose Stornierung bis ${formatDate(date, zone)} um ${formatTime(date, zone)} Uhr`;
    }
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CalendarCheck size={20} className="mr-2 inline shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== hint) {
    t2 = <div className="flex overflow-hidden border-primary/15 border-b py-4 font-base-copy sm:mr-11">{t1}<span>{hint}</span></div>;
    $[1] = hint;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
type SelectedOptionsProps = {
  selectionInfo: ExperienceSelectionInfo[];
  currency: string;
};
function SelectedOptions(t0) {
  const $ = _c(7);
  const {
    selectionInfo,
    currency
  } = t0;
  let t1;
  if ($[0] !== currency || $[1] !== selectionInfo) {
    let t2;
    if ($[3] !== currency) {
      t2 = t3 => {
        const {
          count,
          name,
          price,
          id
        } = t3;
        return <li key={id} className="flex font-body-small"><span className="grow"><AppPriceCategoryCount count={count} category={name} price={price} currency={currency} /></span><span className="font-body-small font-semibold"><Price amount={price * count} currency={currency} /></span></li>;
      };
      $[3] = currency;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = selectionInfo.map(t2);
    $[0] = currency;
    $[1] = selectionInfo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <ul>{t1}</ul>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function ExperienceDuration(t0) {
  const $ = _c(2);
  const {
    duration
  } = t0;
  const formatted = useExperienceDuration(duration);
  let t1;
  if ($[0] !== formatted) {
    t1 = <span>{formatted}</span>;
    $[0] = formatted;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function select({
  validateTimeslot
}: {
  validateTimeslot: boolean;
}) {
  return validateTimeslot;
}